/**
 * What the sidebar shows (ADR 0031). Two parts, split by a line:
 *
 *   Dashboard · Chat assistant · Agency (Team, Statistics, Payouts, Payments) · Tools (Copy, Bulk Media, Audio, Dynamic lists,
 *   Tracking links, Mass messages, Priority mass messages, Subscription messages, Online messages)
 *   ───────────
 *   Workspace · Accounts (AI | CRM)
 *
 * Every entry is shown only when the login can use it; an empty group is not
 * shown at all, so a CRM-only chatter sees just Workspace and their Accounts.
 * Pure so the rules are testable without rendering the shell.
 */

export type NavGroupId = 'agency' | 'tools';

export interface NavItem {
  to: string;
  icon: string;
  /** A `common` namespace key. */
  labelKey: string;
  badge?: number;
  /** Still in beta: the sidebar shows a Beta pill. */
  beta?: boolean;
}

export interface NavGroup {
  id: NavGroupId;
  icon: string;
  labelKey: string;
  items: NavItem[];
}

/** What decides the entries. `null` = still loading (hidden until known). */
export interface SidebarAccess {
  /** The login has at least one console (AI) Account; null while loading. */
  hasConsoleAccounts: boolean | null;
  /** The CRM access gate answered 'ok'. */
  crmOk: boolean;
  /** The CRM access gate answered 'none' (no CRM Accounts at all). */
  crmNone: boolean;
  teamAvailable: boolean | null;
  canSeeStats: boolean | null;
  /** The Payouts page: Statements at Full on at least one Account. */
  canSeePayouts?: boolean | null;
  canAccessPayments: boolean;
  tools: ToolAccess;
  workspaceEnabled: boolean;
  workspaceBadge: number;
}

/** One answer per Tool: can this login use it on at least one Account. */
export interface ToolAccess {
  copy: boolean;
  bulkMedia: boolean;
  audio: boolean;
  dynamicLists: boolean;
  trackingLinks?: boolean;
  /** The Mass messages pages: the login may open that tab on at least one Account. */
  massMessages?: boolean;
  priorityMassMessages?: boolean;
  /** The Subscription messages and Online messages pages (the Account's Automated
   *  messages tab, split in two): the login may open that tab on at least one Account. */
  automatedMessages?: boolean;
  /** The Chat assistant page (the Account's Chat Assistant tab, on its own page): the
   *  login may open that tab on at least one Account. */
  chatAssistant?: boolean;
}

/** A Mass messages page offers Accounts: null means every Account (super admins);
 *  a missing answer (an older backend) offers none. */
export const offersAny = (ids: string[] | null | undefined): boolean => ids === null || (ids?.length ?? 0) > 0;

export interface SidebarModel {
  dashboard: NavItem | null;
  /** The core feature: its own entry right under the Dashboard, outside the groups. */
  chatAssistant: NavItem | null;
  groups: NavGroup[];
  workspace: NavItem | null;
}

export const DASHBOARD_ITEM: NavItem = { to: '/dashboard', icon: 'house', labelKey: 'layout.dashboard' };

export const CHAT_ASSISTANT_ITEM: NavItem = { to: '/chat-assistant', icon: 'message-circle', labelKey: 'layout.chatAssistant' };

export function sidebarModel(access: SidebarAccess): SidebarModel {
  // A CRM-only login has nothing on the Dashboard (it lists console Accounts):
  // its sidebar starts at Workspace.
  const crmOnly = access.hasConsoleAccounts === false && access.crmOk;
  // A login with no Accounts anywhere still gets Payments: its wallet, top-ups and
  // Referral program don't need an Account.
  const noAccounts = access.hasConsoleAccounts === false && access.crmNone;

  const agency: NavItem[] = [];
  if (access.teamAvailable === true) agency.push({ to: '/team', icon: 'users', labelKey: 'layout.team' });
  if (access.canSeeStats === true) agency.push({ to: '/statistics', icon: 'bar-chart-2', labelKey: 'layout.statistics' });
  if (access.canSeePayouts === true) agency.push({ to: '/payouts', icon: 'hand-coins', labelKey: 'layout.payouts' });
  if (access.canAccessPayments || noAccounts) agency.push({ to: '/payments', icon: 'dollar-sign', labelKey: 'layout.payments' });

  const tools: NavItem[] = [];
  if (access.tools.copy) tools.push({ to: '/copy', icon: 'copy', labelKey: 'layout.copy' });
  if (access.tools.bulkMedia) tools.push({ to: '/bulk-media', icon: 'upload-cloud', labelKey: 'layout.bulkMedia' });
  if (access.tools.audio) tools.push({ to: '/audio', icon: 'mic', labelKey: 'layout.audio' });
  if (access.tools.dynamicLists) tools.push({ to: '/dynamic-lists', icon: 'list-filter', labelKey: 'layout.dynamicLists' });
  if (access.tools.trackingLinks) tools.push({ to: '/tracking-links', icon: 'link', labelKey: 'layout.trackingLinks' });
  if (access.tools.massMessages) tools.push({ to: '/mass-messages', icon: 'send', labelKey: 'layout.massMessages' });
  if (access.tools.priorityMassMessages) tools.push({ to: '/priority-mass-messages', icon: 'zap', labelKey: 'layout.priorityMassMessages' });
  if (access.tools.automatedMessages) {
    tools.push({ to: '/subscription-messages', icon: 'user-plus', labelKey: 'layout.subscriptionMessages' });
    tools.push({ to: '/online-messages', icon: 'radio', labelKey: 'layout.onlineMessages' });
  }

  const groups: NavGroup[] = [];
  if (agency.length) groups.push({ id: 'agency', icon: 'building-2', labelKey: 'layout.agency', items: agency });
  if (tools.length) groups.push({ id: 'tools', icon: 'tool', labelKey: 'layout.tools', items: tools });

  return {
    dashboard: crmOnly ? null : DASHBOARD_ITEM,
    chatAssistant: access.tools.chatAssistant ? CHAT_ASSISTANT_ITEM : null,
    groups,
    workspace: access.workspaceEnabled
      ? { to: '/workspace', icon: 'layout-dashboard', labelKey: 'layout.workspace', badge: access.workspaceBadge }
      : null,
  };
}

/** Every destination in sidebar order, for the phone tab bar. */
export function flatItems(model: SidebarModel): NavItem[] {
  return [
    ...(model.dashboard ? [model.dashboard] : []),
    ...(model.chatAssistant ? [model.chatAssistant] : []),
    ...model.groups.flatMap((g) => g.items),
    ...(model.workspace ? [model.workspace] : []),
  ];
}

/** Phones keep Menu plus the four destinations used most, in this order. */
const TAB_ORDER = ['/dashboard', '/chat-assistant', '/workspace', '/statistics', '/team', '/payouts', '/payments', '/copy', '/bulk-media', '/audio', '/dynamic-lists', '/tracking-links', '/mass-messages', '/priority-mass-messages', '/subscription-messages', '/online-messages'];

/**
 * With the accounts corner (a login with CRM Accounts) the bar keeps three:
 * the corner takes Team's place and Team stays in the drawer.
 */
export function phoneTabs(model: SidebarModel, accountsCorner = false): NavItem[] {
  return [...flatItems(model)]
    .filter((item) => !accountsCorner || item.to !== '/team')
    .sort((a, b) => TAB_ORDER.indexOf(a.to) - TAB_ORDER.indexOf(b.to))
    .slice(0, accountsCorner ? 3 : 4);
}

/** The group holding the current page, so it opens even when folded away. */
export function groupOf(model: SidebarModel, pathname: string): NavGroupId | null {
  return model.groups.find((g) => g.items.some((i) => i.to === pathname))?.id ?? null;
}

/**
 * Where "/" (and the post-login redirect) lands. null = not known yet.
 * A login with console Accounts starts on the Dashboard; a CRM-only one on Workspace.
 */
export function homePath(access: Pick<SidebarAccess, 'hasConsoleAccounts' | 'crmOk' | 'workspaceEnabled'>, crmSettled: boolean): string | null {
  if (access.hasConsoleAccounts === null) return null;
  if (access.hasConsoleAccounts) return '/dashboard';
  if (!crmSettled) return null;
  return access.crmOk && access.workspaceEnabled ? '/workspace' : '/dashboard';
}
